/**
 * Unit-aware display for the month-end forecast.
 *
 * The page carries a gallons/dollars toggle, and the two are not
 * interchangeable: gallons are exact everywhere, while dollars are actual FOB on
 * the shipped and committed layers and a flat rate on the modelled one. So a
 * dollar figure is shown only where the payload actually holds one —
 * `money(null)` renders an em dash rather than a zero, which is what the missing
 * shipped/committed split must look like.
 */

import type { Cell } from '../../lib/types'

export type Unit = 'gal' | 'usd'

/** The dash used wherever the source has no figure. Never a zero. */
export const NONE = '—'

/** A cell as a finite number, or null. Blank strings and nulls collapse. */
export function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/** Whole gallons with thousands separators. */
export function gallons(value: number | null): string {
  if (value === null) return NONE
  return Math.round(value).toLocaleString('en-US')
}

/** Whole dollars. */
export function money(value: number | null): string {
  if (value === null) return NONE
  return `$${Math.round(value).toLocaleString('en-US')}`
}

/** Dollars to the cent — for a per-gallon rate, where whole dollars lose it. */
export function rate(value: number | null): string {
  if (value === null) return NONE
  return `$${value.toFixed(2)}`
}

/** A compact axis label: 1.68M / 240k / 0. */
export function compact(value: number, unit: Unit): string {
  const prefix = unit === 'usd' ? '$' : ''
  if (value === 0) return '0'
  if (Math.abs(value) >= 1_000_000) return `${prefix}${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `${prefix}${Math.round(value / 1_000).toLocaleString()}k`
  return `${prefix}${Math.round(value).toLocaleString()}`
}

/** One signed percentage, to a tenth. */
export function signedPct(value: number | null): string {
  if (value === null) return NONE
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
}

/** "2026-09-30" -> "30 Sep". Anything unparseable comes back unchanged. */
export function dayLabel(iso: string | null): string {
  if (iso === null) return ''
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/** A signed whole-gallon difference: +1,204 / -5,238 / 0. */
export function signedGallons(value: number | null): string {
  if (value === null) return NONE
  return Math.round(value).toLocaleString('en-US', { signDisplay: 'exceptZero' })
}

/** A signed dollar variance, for a comparison against plan. */
export function signedMoney(value: number | null): string {
  if (value === null) return NONE
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}$${Math.abs(Math.round(value)).toLocaleString('en-US')}`
}
